import { Check, LockSimple, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useState } from "react";
import { api } from "@/lib/api";
import { pct } from "@/components/deals/format";

export function ApproveCell({ r, onDataChanged }) {
  const [busy, setBusy] = useState(false);
  const approved = !!r.offer_approved && r.approved_offer != null;
  const candidate = r.effective_offer;
  const smart = r.smart_offer?.available ? r.smart_offer.recommended : null;
  const legacyAuto = r.suggested_offer?.available
    ? (r.suggested_offer.mode === "max_buy" ? r.suggested_offer.max_buy_at_target_roi : (r.buy_cost != null ? r.buy_cost : r.suggested_offer.recommended))
    : null;
  const candidateLabel = r.offer_is_manual ? "your adjusted offer" : r.offer_is_smart ? "SMART OFFER" : "auto offer";
  const act = async (price) => {
    setBusy(true);
    try {
      if (approved) { await api.unapproveDealOffer(r.id); toast.info(`${r.set_number} unlocked`); }
      else { await api.approveDealOffer(r.id, price); toast.success(`${r.set_number} approved @ $${Number(price).toFixed(2)}`); }
      onDataChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    setBusy(false);
  };
  if (!r.set_number) return <td className="px-3 py-2" />;
  return (
    <td className="px-3 py-2 text-center whitespace-nowrap" data-testid={`approve-cell-${r.set_number}`}>
      {approved ? (
        <button onClick={() => act()} disabled={busy} data-testid={`approve-undo-${r.set_number}`}
          title={`Approved ${r.approved_at ? new Date(r.approved_at).toLocaleString() : ""} — click to unlock`}
          className="inline-flex items-center gap-1 px-2 py-1 border border-[#00A850]/60 bg-[#00A850]/[0.10] text-[#00A850] rounded-sm font-mono text-[10px] font-bold hover:bg-[#00A850]/[0.18]">
          <LockSimple size={10} weight="fill" /> ${Number(r.approved_offer).toFixed(2)}
        </button>
      ) : (
        <span className="inline-flex items-center gap-1">
          <button onClick={() => act(candidate)} disabled={busy || !(candidate > 0)} data-testid={`approve-btn-${r.set_number}`}
            title={candidate > 0 ? `Approve at $${Number(candidate).toFixed(2)} (${candidateLabel})` : "No offer to approve yet"}
            className={`inline-flex items-center gap-1 px-2 py-1 border rounded-sm font-mono text-[10px] font-bold hover:border-[#00A850] hover:text-[#00A850] disabled:opacity-30 ${r.offer_is_smart ? "border-[#E3000B]/50 text-[#E3000B]" : "border-[#E4E4E0] text-gray-600"}`}>
            <Check size={10} weight="bold" /> {candidate > 0 ? `$${Number(candidate).toFixed(2)}` : "--"}
          </button>
          {smart != null && smart !== candidate && (
            <button onClick={() => act(smart)} disabled={busy} data-testid={`approve-smart-${r.set_number}`}
              title={`Approve at the SMART OFFER $${smart.toFixed(2)}`}
              className="px-1.5 py-1 border border-[#E4E4E0] text-gray-500 rounded-sm font-mono text-[9px] hover:border-[#E3000B] hover:text-[#E3000B]">
              SMART
            </button>
          )}
          {r.offer_is_smart && legacyAuto > 0 && legacyAuto !== candidate && (
            <button onClick={() => act(legacyAuto)} disabled={busy} data-testid={`approve-auto-${r.set_number}`}
              title={`Approve at the legacy auto offer $${Number(legacyAuto).toFixed(2)} (max-buy / cost basis)`}
              className="px-1.5 py-1 border border-[#E4E4E0] text-gray-500 rounded-sm font-mono text-[9px] hover:border-zinc-300 hover:text-gray-900">
              AUTO
            </button>
          )}
        </span>
      )}
    </td>
  );
}

const VERDICT_STYLE = {
  go:        { cls: "border-[#00A850]/50 text-[#00A850] bg-[#00A850]/[0.08]", hint: "Offer this — it's your % of PriceCharting AND still clears your min ROI on Amazon" },
  capped:    { cls: "border-[#D97706]/50 text-[#D97706] bg-[#D97706]/[0.08]", hint: "Your % of PriceCharting was too rich for Amazon — capped at the price that still hits min ROI" },
  pass:      { cls: "border-[#FF3333]/50 text-[#FF3333] bg-[#FF3333]/[0.06]", hint: "No profitable price at your min ROI — pass unless something changes" },
  unchecked: { cls: "border-zinc-600 text-gray-600 bg-gray-200/40", hint: "No Amazon Buy Box yet — scan Amazon to verify profitability" },
};

/** SMART OFFER — % of PriceCharting sealed, gated by min ROI on Amazon after fees. */
export function SmartOfferCell({ so, setNumber }) {
  if (!so || !so.available) {
    return <td className="px-3 py-2 text-right" data-testid={`smart-${setNumber}`}><span className="text-gray-400" title="Need a PriceCharting (or BrickLink) price first — run SCAN PRICECHARTING">--</span></td>;
  }
  const v = VERDICT_STYLE[so.verdict] || VERDICT_STYLE.unchecked;
  const tip = [
    `SMART OFFER: $${so.recommended.toFixed(2)} · ${so.verdict_label}`,
    "",
    `${so.pct.toFixed(0)}% of ${so.basis_source} $${so.basis.toFixed(2)} = $${so.raw.toFixed(2)}`,
    so.buy_box != null ? `Amazon ${so.listed_price_basis ? "listed/avg price" : "Buy Box"} $${so.buy_box.toFixed(2)} → nets $${so.net_sell.toFixed(2)} after fees + costs` : "Amazon Buy Box: none yet",
    so.max_at_min_roi != null ? `Max you can pay for ${so.min_roi_pct.toFixed(0)}% ROI: $${so.max_at_min_roi.toFixed(2)}` : null,
    so.roi_pct != null ? `At $${so.recommended.toFixed(2)} → net $${so.net_profit.toFixed(2)} · ROI ${so.roi_pct}%` : null,
    "",
    ...(so.notes || []),
    "",
    v.hint,
  ].filter(x => x !== null).join("\n");
  return (
    <td className="px-3 py-2 text-right whitespace-nowrap" data-testid={`smart-${setNumber}`}>
      <span className="inline-flex flex-col items-end leading-tight" title={tip}>
        <span className="font-bold text-gray-900" data-testid={`smart-price-${setNumber}`}>${so.recommended.toFixed(2)}</span>
        <span className={`mt-0.5 inline-flex items-center gap-1 px-1 py-px border rounded-sm font-mono text-[8px] font-bold ${so.listed_price_basis ? "border-[#D97706] text-[#D97706] bg-[#D97706]/10" : v.cls}`} data-testid={`smart-verdict-${setNumber}`}
          title={so.listed_price_basis ? "No Amazon Buy Box — ROI checked against the listed / average price instead" : undefined}>
          {so.verdict_label}{so.roi_pct != null ? ` · ROI ${Math.round(so.roi_pct)}%` : ""}
        </span>
        <span className="text-[8px] font-mono text-gray-500 whitespace-nowrap">
          {so.pct_of_basis != null ? `${Math.round(so.pct_of_basis)}%` : `${Math.round(so.pct)}%`} of {so.basis_source.startsWith("Price") ? "PC" : "BL"} ${so.basis.toFixed(0)}
        </span>
      </span>
    </td>
  );
}

/** Suggested offer or max-buy price — dual mode. A manual adjustment wins and shows an "M" badge. */
export function SuggestedOfferCell({ offer, setNumber, manualOffer, onEdit }) {
  const pencil = (
    <button onClick={onEdit} title="Adjust this offer" data-testid={`offer-edit-${setNumber}`}
      className="text-gray-500 hover:text-[#E3000B] shrink-0"><PencilSimple size={11} weight="bold" /></button>
  );
  if (manualOffer != null) {
    const autoTxt = offer?.available ? ` · auto was $${(offer.mode === "max_buy" ? offer.max_buy_at_target_roi : offer.recommended).toFixed(2)}` : "";
    return (
      <td className="px-3 py-2 text-right" data-testid={`suggested-${setNumber}`}>
        <span className="inline-flex items-center gap-1.5 justify-end">
          <span className="inline-flex flex-col items-end leading-tight" title={`Manually adjusted offer${autoTxt}`}>
            <span className="font-bold text-[#E3000B]" data-testid={`manual-offer-${setNumber}`}>${Number(manualOffer).toFixed(2)}</span>
            <span className="text-[9px] font-mono text-[#E3000B]/80 inline-flex items-center gap-1">
              <span className="px-1 border border-[#E3000B]/60 rounded-sm text-[8px] font-bold">M</span> ADJUSTED
            </span>
          </span>
          {pencil}
        </span>
      </td>
    );
  }
  if (!offer || !offer.available) {
    const label = offer && offer.reason === "no_market_price" ? "NO MKT" : "--";
    const hint = offer && offer.reason === "no_market_price"
      ? "No Amazon Buy Box or BrickLink price yet — scan first"
      : "Add a buy cost to this deal to see a recommended list price";
    return (
      <td className="px-3 py-2 text-right" data-testid={`suggested-${setNumber}`}>
        <span className="inline-flex items-center gap-1.5"><span className="text-gray-400" title={hint}>{label}</span>{pencil}</span>
      </td>
    );
  }

  // MAX BUY mode — we don't know cost yet, tell the user what to pay.
  if (offer.mode === "max_buy") {
    const feasible = offer.feasible;
    const tooltip = [
      `MAX BUY MODE — no buy cost yet`,
      `Assumed sell: $${offer.assumed_sell_price.toFixed(2)}`,
      `Max buy @ ${offer.target_roi_pct}% ROI: $${offer.max_buy_at_target_roi.toFixed(2)}`,
      `Max buy @ break-even: $${offer.max_buy_at_break_even.toFixed(2)}`,
      "",
      ...(offer.notes || []),
    ].filter(Boolean).join("\n");
    return (
      <td className="px-3 py-2 text-right" data-testid={`suggested-${setNumber}`}>
        <span className="inline-flex items-center gap-1.5 justify-end">
        <span className="inline-flex flex-col items-end leading-tight" title={tooltip}>
          <span className={`font-bold ${feasible ? "text-[#00A850]" : "text-[#FF3333]"}`}>
            ≤ ${offer.max_buy_at_target_roi.toFixed(2)}
          </span>
          <span className={`text-[9px] font-mono ${feasible ? "text-[#00A850]/70" : "text-[#FF3333]/70"}`}>
            MAX BUY · {offer.target_roi_pct}% ROI
          </span>
        </span>
        {pencil}
        </span>
      </td>
    );
  }

  // OFFER mode — buy cost known, recommend a list price. ROI shown is the
  // real-market figure at the current Buy Box (falls back to @recommended
  // only when Amazon has no live Buy Box).
  const profitable = offer.profitable;
  const hasBB = offer.roi_at_buy_box != null;
  const roi = hasBB ? offer.roi_at_buy_box : offer.roi_at_recommended;
  const tooltip = [
    `Recommended: $${offer.recommended.toFixed(2)}`,
    `Break-even: $${offer.break_even.toFixed(2)}`,
    `Target ROI: $${offer.target.toFixed(2)}`,
    offer.bl_ceiling != null ? `BL ceiling: $${offer.bl_ceiling.toFixed(2)}` : null,
    offer.bb_ceiling != null ? `Buy Box − undercut: $${offer.bb_ceiling.toFixed(2)}` : null,
    "",
    hasBB ? `Net @ Buy Box: $${offer.net_profit_at_buy_box.toFixed(2)}` : null,
    hasBB ? `ROI @ Buy Box: ${offer.roi_at_buy_box.toFixed(1)}%` : null,
    `Net @ recommended: $${offer.net_profit_at_recommended.toFixed(2)}`,
    offer.roi_at_recommended != null ? `ROI @ recommended: ${offer.roi_at_recommended.toFixed(1)}%` : null,
    "",
    ...(offer.notes || []),
  ].filter(Boolean).join("\n");

  return (
    <td className="px-3 py-2 text-right" data-testid={`suggested-${setNumber}`}>
      <span className="inline-flex items-center gap-1.5 justify-end">
      <span className="inline-flex flex-col items-end leading-tight" title={tooltip}>
        <span className={`font-bold ${profitable ? "text-[#00A850]" : "text-[#FF3333]"}`}>
          ${offer.recommended.toFixed(2)}
        </span>
        {roi != null && (
          <span className={`text-[9px] font-mono ${profitable ? "text-[#00A850]/80" : "text-[#FF3333]/80"}`}
                data-testid={`suggested-roi-${setNumber}`}>
            {roi >= 0 ? "+" : ""}{roi.toFixed(1)}% ROI{hasBB ? " @ BB" : ""}
          </span>
        )}
      </span>
      {pencil}
      </span>
    </td>
  );
}


